import { useEffect, useMemo, useState } from 'react';
import { Button, Spinner, useDisclosure } from '@heroui/react';
import type { RowSelectionState } from '@tanstack/react-table';
import { PageHeader } from '@/components/common/PageHeader';
import { Pagination } from '@/components/common/Pagination';
import { ErrorState } from '@/components/common/ErrorState';
import { useTableQueryState } from '@/hooks/useTableQueryState';
import {
  useQueueOverview, useQueueMessages, useQueueMessageAction, useQueueControl,
} from '@/features/queue/hooks';
import { QueueOverview } from '@/features/queue/components/QueueOverview';
import { QueueToolbar } from '@/features/queue/components/QueueToolbar';
import { QueueMessagesTable } from '@/features/queue/components/QueueMessagesTable';
import { QueueDetailsDrawer } from '@/features/queue/components/QueueDetailsDrawer';
import { QueueStateChip } from '@/features/queue/components/QueueChips';
import { notify } from '@/lib/notify';
import { downloadCsv } from '@/lib/exportCsv';
import type { QueueMessage, QueueMessageActionType } from '@/features/queue/types';
import {
  PauseCircleIcon, PlayCircleIcon, ArrowPathIcon, ArrowDownTrayIcon,
  BoltIcon, ArrowsRightLeftIcon, TrashIcon,
} from '@/icons';

const FILTER_KEYS = ['queue', 'status', 'priority', 'tenant', 'sender', 'recipient', 'policy', 'threat', 'age'];
const DEFAULT_BUCKET = 'deferred';
/** Row/bulk "move" parks messages in the hold bucket for review. */
const MOVE_TARGET = 'hold';

/**
 * Mail Queue & Delivery Center (Sprint 4.9). Extends the existing queue module
 * into a full operations surface: KPI + throughput + bucket dashboard, a
 * searchable/filterable message table with per-row and bulk actions, per-queue
 * pause/resume/flush controls, and the shared Investigation Drawer (delivery
 * timeline, SMTP conversation, history and correlated policy/spam/threat/
 * attachment/URL/alert summaries). Reuses StatTile, charts, DataTable, Tabs,
 * InvestigationDrawer, SearchInput, DebouncedInput, Pagination, notify and RBAC.
 * 15–20s polling; `security.view` / `security.manage`.
 */
export function QueuePage() {
  const { state, setPage, setPageSize, setSearch, toggleSort, setFilter, reset } = useTableQueryState(FILTER_KEYS);
  const f = state.filters;
  const bucket = f.queue || DEFAULT_BUCKET;

  const overview = useQueueOverview();
  const { data, isLoading, isError, refetch } = useQueueMessages({
    page: state.page, pageSize: state.pageSize, q: state.q || undefined, sort: state.sort || undefined,
    queue: bucket, status: f.status, priority: f.priority, tenant: f.tenant,
    sender: f.sender, recipient: f.recipient, policy: f.policy, threat: f.threat, age: f.age,
  });

  const act = useQueueMessageAction();
  const control = useQueueControl();

  const drawer = useDisclosure();
  const [active, setActive] = useState<QueueMessage | null>(null);
  const openDetails = (m: QueueMessage) => { setActive(m); drawer.onOpen(); };

  const [selection, setSelection] = useState<RowSelectionState>({});
  const selectedIds = useMemo(() => Object.keys(selection).filter((k) => selection[k]), [selection]);
  // Selection is per-view: clear it when the bucket or page changes.
  useEffect(() => { setSelection({}); }, [bucket, state.page]);

  const rows = data?.data ?? [];
  const bucketInfo = overview.data?.buckets.find((b) => b.key === bucket);

  const runRow = (action: QueueMessageActionType, m: QueueMessage) =>
    act.mutate({ action, queueIds: [m.queueId], targetQueue: action === 'move' ? MOVE_TARGET : undefined });
  const runBulk = (action: QueueMessageActionType) => {
    if (selectedIds.length === 0) return;
    act.mutate({ action, queueIds: selectedIds, targetQueue: action === 'move' ? MOVE_TARGET : undefined },
      { onSuccess: () => setSelection({}) });
  };

  const exportCsv = () => {
    if (rows.length === 0) { notify.info('Nothing to export in this queue.'); return; }
    const cols: Array<[string, (m: QueueMessage) => string]> = [
      ['Queue ID', (m) => m.queueId], ['Sender', (m) => m.sender], ['Recipient', (m) => m.recipient],
      ['Subject', (m) => m.subject], ['Status', (m) => m.status], ['Retries', (m) => String(m.retries)],
      ['Priority', (m) => m.priority], ['Tenant', (m) => m.tenant], ['Arrival', (m) => m.arrivalTime],
      ['Worker', (m) => m.worker ?? ''],
    ];
    downloadCsv(cols.map(([h]) => h), rows.map((m) => cols.map(([, get]) => get(m))),
      `queue-${bucket}-${new Date().toISOString().slice(0, 10)}.csv`);
    notify.success(`Exported ${rows.length} row${rows.length === 1 ? '' : 's'}`);
  };

  return (
    <div>
      <PageHeader
        title="Mail Queue & Delivery Center"
        description="Inspect, retry and manage every inbound, outbound and internal mail queue — with delivery timelines, SMTP traces and cross-links to Policy, Spam, Threat, Attachment and URL investigation."
      />

      <div className="mb-6">
        {overview.isLoading ? (
          <div className="flex justify-center py-12"><Spinner label="Loading delivery center…" /></div>
        ) : overview.isError || !overview.data ? (
          <ErrorState error={overview.error} onRetry={overview.refetch} />
        ) : (
          <QueueOverview data={overview.data} activeBucket={bucket} onBucket={(key) => setFilter('queue', key)} />
        )}
      </div>

      <div className="rounded-xl border border-border bg-surface">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-fg">{bucketInfo?.label ?? 'Deferred'} queue</h2>
            {bucketInfo && <QueueStateChip value={bucketInfo.state} />}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {bucketInfo?.state === 'paused' ? (
              <Button size="sm" variant="flat" startContent={<PlayCircleIcon className="h-4 w-4" />} isLoading={control.isPending}
                onPress={() => control.mutate({ action: 'resume', queue: bucket })}>Resume queue</Button>
            ) : (
              <Button size="sm" variant="flat" startContent={<PauseCircleIcon className="h-4 w-4" />} isLoading={control.isPending}
                onPress={() => control.mutate({ action: 'pause', queue: bucket })}>Pause queue</Button>
            )}
            <Button size="sm" variant="flat" startContent={<ArrowPathIcon className="h-4 w-4" />} isLoading={control.isPending}
              onPress={() => control.mutate({ action: 'flush', queue: bucket })}>Flush queue</Button>
            <Button size="sm" variant="light" startContent={<ArrowDownTrayIcon className="h-4 w-4" />} onPress={exportCsv}>Export</Button>
          </div>
        </div>

        <QueueToolbar
          q={state.q} filters={f}
          onSearch={setSearch} onFilter={setFilter} onClear={reset}
        />

        {selectedIds.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-b border-border bg-brand/5 px-4 py-2">
            <span className="text-sm font-medium text-brand">{selectedIds.length} selected</span>
            <Button size="sm" variant="flat" startContent={<ArrowPathIcon className="h-4 w-4" />} isLoading={act.isPending} onPress={() => runBulk('retry')}>Retry</Button>
            <Button size="sm" variant="flat" startContent={<BoltIcon className="h-4 w-4" />} isLoading={act.isPending} onPress={() => runBulk('force')}>Force delivery</Button>
            <Button size="sm" variant="flat" startContent={<ArrowsRightLeftIcon className="h-4 w-4" />} isLoading={act.isPending} onPress={() => runBulk('move')}>Move to hold</Button>
            <Button size="sm" variant="flat" startContent={<PlayCircleIcon className="h-4 w-4" />} isLoading={act.isPending} onPress={() => runBulk('release')}>Release</Button>
            <Button size="sm" variant="flat" color="danger" startContent={<TrashIcon className="h-4 w-4" />} isLoading={act.isPending} onPress={() => runBulk('delete')}>Delete</Button>
            <Button size="sm" variant="light" className="ml-auto" onPress={() => setSelection({})}>Clear</Button>
          </div>
        )}

        <div className="overflow-x-auto">
          <QueueMessagesTable
            messages={rows} isLoading={isLoading} isError={isError} onRetry={refetch}
            sort={state.sort} onSort={toggleSort} onView={openDetails}
            selection={selection} onSelectionChange={setSelection} onRowAction={runRow}
          />
        </div>
        <Pagination page={state.page} pageSize={state.pageSize} total={data?.total} onPageChange={setPage} onPageSizeChange={setPageSize} />
      </div>

      <QueueDetailsDrawer message={active} isOpen={drawer.isOpen} onClose={drawer.onClose} />
    </div>
  );
}
